<template>
  <main class="category-page">
    <div class="iso-container">
      <h1 class="iso-section-title">{{ $t('nav.category') }}</h1>

      <nav class="category-tabs" aria-label="分类导航">
        <button
          v-for="cat in subCategories"
          :key="cat.slug"
          class="iso-tag category-tab"
          :class="{ active: currentSlug === cat.slug }"
          @click="switchCategory(cat.slug)"
        >
          {{ cat.label }}
        </button>
      </nav>

      <section v-if="articles.length" class="iso-grid">
        <IsometricCard
          v-for="article in articles"
          :key="article.id"
          :id="article.id"
          :title="article.title"
          :summary="article.summary"
          :cover-image="article.cover_image"
          :date="article.published_at"
          :category="article.category"
          :slug="article.slug"
        />
      </section>
      <div v-else-if="!loading" class="empty-state">
        <p>{{ $t('article.noArticles') }}</p>
      </div>
      <div v-if="loading" class="iso-loading">{{ $t('common.loading') }}</div>
      <div v-if="hasMore && !loading" class="load-more">
        <button class="iso-btn" @click="loadMore">{{ $t('common.loadMore') }}</button>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { request } from '@/api'
import IsometricCard from '@/components/IsometricCard.vue'

interface Article {
  id: number
  title: string
  slug: string
  summary?: string
  category?: string
  cover_image?: string
  published_at?: string
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const subCategories = [
  { slug: 'gaokao', label: t('category.gaokao') },
  { slug: 'zhongkao', label: t('category.zhongkao') },
  { slug: 'kaoyan', label: t('category.kaoyan') },
  { slug: 'gongwuyuan', label: t('category.gongwuyuan') },
  { slug: 'zhiye', label: t('category.zhiye') },
  { slug: 'yingyu', label: t('category.yingyu') },
  { slug: 'liuxue', label: t('category.liuxue') },
]

const currentSlug = ref(route.params.slug as string || 'gaokao')
const articles = ref<Article[]>([])
const loading = ref(false)
const hasMore = ref(true)
const page = ref(1)
const pageSize = 12

async function fetchArticles(append = false) {
  if (loading.value) return
  loading.value = true
  try {
    const res = await request<{ data: Article[] }>(`/news?limit=${pageSize}&offset=${(page.value - 1) * pageSize}&category=${currentSlug.value}`)
    const list = res.data || []
    if (append) {
      articles.value.push(...list)
    } else {
      articles.value = list
    }
    hasMore.value = list.length >= pageSize
  } catch {
    hasMore.value = false
  } finally {
    loading.value = false
  }
}

function switchCategory(slug: string) {
  currentSlug.value = slug
  page.value = 1
  router.replace(`/category/${slug}`)
  fetchArticles()
}

function loadMore() {
  page.value++
  fetchArticles(true)
}

watch(() => route.params.slug, (slug) => {
  if (slug && slug !== currentSlug.value) {
    currentSlug.value = slug as string
    page.value = 1
    fetchArticles()
  }
})

onMounted(() => {
  fetchArticles()
})
</script>

<style scoped>
.category-page {
  padding-top: calc(var(--header-height) + 32px);
  padding-bottom: 48px;
}

.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
}

.category-tab {
  cursor: pointer;
  font-size: 13px;
  padding: 6px 16px;
  background: none;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 20px;
  color: var(--iso-text-dim);
  transition: all 0.2s;
}

.category-tab:hover {
  color: var(--iso-cyan);
  border-color: rgba(0, 212, 255, 0.4);
}

.category-tab.active {
  background: rgba(0, 212, 255, 0.15);
  border-color: var(--iso-cyan);
  color: var(--iso-cyan);
  box-shadow: 0 0 12px var(--iso-cyan-glow);
}

.empty-state {
  text-align: center;
  padding: 64px 24px;
  color: var(--iso-text-dim);
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}
</style>
